import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Footer = () => {
  const { language } = useLanguage();
  const { lang } = useParams<{ lang: string }>();

  const content = {
    en: {
      copyright: '© 2025 QRCodeForge.com - Create, customize and manage your QR codes with ease. Free, fast, and secure.',
      links: {
        product: 'Product',
        company: 'Company',
        support: 'Support',
        legal: 'Legal',
        home: 'Home',
        about: 'About Us',
        help: 'Help & Documentation',
        contact: 'Contact',
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service'
      },
      description: 'QRCodeForge is a free online QR code generator that creates high-quality QR codes for URLs, text, email, WiFi, and social media. Generate custom QR codes instantly with our secure, client-side processing.'
    },
    zh: {
      copyright: '© 2025 QRCodeForge.com - 轻松创建、自定义和管理您的二维码。免费、快速、安全。',
      links: {
        product: '产品',
        company: '公司',
        support: '支持',
        legal: '法律',
        home: '首页',
        about: '关于我们',
        help: '帮助文档',
        contact: '联系我们',
        privacyPolicy: '隐私政策',
        termsOfService: '使用条款'
      },
      description: 'QRCodeForge 是一个免费的在线二维码生成器，为网址、文本、邮件、WiFi 和社交媒体创建高质量二维码。使用我们安全的客户端处理技术即时生成自定义二维码。'
    }
  };

  const currentContent = content[language];

  return (
    <footer className="bg-slate-800/50 backdrop-blur-sm border-t border-white/10 mt-16">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">

          {/* Company Info */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-bold text-lg mb-4">QRCodeForge</h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              {currentContent.description}
            </p>
            <p className="text-blue-300 text-sm">
              {language === 'zh' ? '邮箱：' : 'Email: '}
              <a href="mailto:support@qrcodeforge.com" className="hover:text-blue-200 transition-colors">
                support@qrcodeforge.com
              </a>
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{currentContent.links.product}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={`/${lang}`}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {currentContent.links.home}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${lang}/help`}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {currentContent.links.help}
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{currentContent.links.company}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={`/${lang}/about`}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {currentContent.links.about}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">

            {/* Copyright */}
            <div className="mb-4 md:mb-0">
              <p className="text-gray-400 text-sm text-center md:text-left">
                {currentContent.copyright}
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center md:justify-end gap-6">
              <Link
                to={`/${lang}/privacy-policy`}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                {currentContent.links.privacyPolicy}
              </Link>
              <Link
                to={`/${lang}/terms-of-service`}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                {currentContent.links.termsOfService}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;